import { Injectable } from '@angular/core';
import { StateCreator, StoreApi } from 'zustand/vanilla';
import { PersonApiService } from '../../api/person-api.service';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { Person } from '../../api/person-api.class';
import {
  GetPersonsInput,
  Spindeigenschaften,
} from '../../api/person-api.types';
import { ClothingApiService } from '../../api/clothing-api.service';
import { GetClothingOutput } from '../../api/clothing-api.types';

export interface PersonsState {
  activePerson: Person | null;
  activePersonClothing: GetClothingOutput[];
  activePersonLockers: Spindeigenschaften[];
  filteredPersons: Person[];
}

export interface PersonsSlice {
  persons: PersonsState;
}

export interface PersonsActions {
  setActivePerson: (activePersonId: number) => Promise<void>;
  getActivePerson: () => Person | null;
  setFilteredPersons: (params: GetPersonsInput) => Promise<void>;
  // Helper function to set filtered persons from client side
  setFilteredPersonsClient: (filtered: Person[]) => Promise<void>;
  getFilteredPersons: () => Person[];
  reset: () => void;
}

const initialPersonsState: PersonsState = {
  activePerson: null,
  activePersonClothing: [],
  activePersonLockers: [],
  filteredPersons: [],
};

@Injectable()
export class PersonsStoreService {
  private slice: StateCreator<PersonsSlice>;
  private actions: (store: StoreApi<PersonsSlice>) => PersonsActions;

  constructor(
    personApiService: PersonApiService,
    clothingApiService: ClothingApiService,
  ) {
    this.slice = () => ({
      persons: initialPersonsState,
    });

    this.actions = (store) => ({
      async setActivePerson(activePersonId) {
        const activePerson = new Person(
          await firstValueFrom(personApiService.getPerson(activePersonId)),
        );

        // get clothing for active person
        const clothingResults: GetClothingOutput[] = await firstValueFrom(
          clothingApiService.getAllClothing({
            personId: activePersonId,
          }),
        );

        store.setState((state) => ({
          persons: {
            ...state.persons,
            activePerson: activePerson,
            activePersonLockers: activePerson.spinde,
            activePersonClothing: clothingResults,
          },
        }));
      },

      async setFilteredPersons(params: GetPersonsInput) {
        const filteredPersons = Person.fromResultArray(
          await lastValueFrom(personApiService.getAllPersons(params)),
        );
        store.setState((state) => ({
          persons: {
            ...state.persons,
            filteredPersons: filteredPersons || [],
          },
        }));
      },

      async setFilteredPersonsClient(filtered: Person[]) {
        store.setState((state) => ({
          persons: {
            ...state.persons,
            filteredPersons: filtered || [],
          },
        }));
      },

      getFilteredPersons() {
        return store.getState().persons.filteredPersons;
      },

      getActivePerson() {
        return store.getState().persons.activePerson;
      },

      reset() {
        store.setState(() => ({
          persons: initialPersonsState,
        }));
      },
    });
  }

  getSlice() {
    return this.slice;
  }

  getActions() {
    return this.actions;
  }
}
